import type {
  QuickPropertyUnitRow,
  QuickPropertyUnitSavePlan,
} from "../../../overlays/propertyUnitsQuickEdit/contract"

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()

export const residentialUnitRowsForProperty = (property: Property): QuickPropertyUnitRow[] =>
  (property.units ?? [])
    .filter((unit) => unit.unit_type === "RESIDENTIAL")
    .map((unit) => {
      const unitNumber = normalizeText(unit.unit_number)
      const unitLabel = normalizeText(unit.unit_label || unit.label || unitNumber)

      return {
        id: unit.id,
        clientId: unit.id,
        unitNumber,
        unitLabel,
        originalUnitLabel: unitLabel,
        isNew: false,
      }
    })

export const createBlankUnitRow = (): QuickPropertyUnitRow => ({
  clientId:
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? `new-unit:${crypto.randomUUID()}`
      : `new-unit:${Date.now()}:${Math.random().toString(36).slice(2)}`,
  unitNumber: "",
  unitLabel: "",
  isNew: true,
})

export const normalizeQuickUnitRow = (row: QuickPropertyUnitRow): QuickPropertyUnitRow => ({
  ...row,
  unitNumber: normalizeText(row.unitNumber),
  unitLabel: normalizeText(row.unitLabel),
  originalUnitLabel:
    row.originalUnitLabel == null ? row.originalUnitLabel : normalizeText(row.originalUnitLabel),
})

export const quickUnitRowValidationError = (row: QuickPropertyUnitRow): string | null => {
  const normalized = normalizeQuickUnitRow(row)

  if (!normalized.isNew) return null

  if (!normalized.unitNumber) return "Bitte eine Einheitsnummer angeben."

  return null
}

export const hasQuickUnitRowChanges = (row: QuickPropertyUnitRow): boolean => {
  const normalized = normalizeQuickUnitRow(row)

  if (normalized.isNew) return Boolean(normalized.unitNumber || normalized.unitLabel)

  return normalized.unitLabel !== normalizeText(normalized.originalUnitLabel)
}

export const buildQuickUnitSavePlan = ({
  propertyId,
  rows,
}: {
  propertyId: string
  rows: QuickPropertyUnitRow[]
}): QuickPropertyUnitSavePlan => {
  const creates: QuickPropertyUnitSavePlan["creates"] = []
  const updates: QuickPropertyUnitSavePlan["updates"] = []

  for (const sourceRow of rows) {
    const row = normalizeQuickUnitRow(sourceRow)

    if (row.isNew) {
      if (!row.unitNumber && !row.unitLabel) continue

      const validationError = quickUnitRowValidationError(row)

      if (validationError) continue

      creates.push({
        clientId: row.clientId,
        payload: {
          property_id: propertyId,
          unit_type: "RESIDENTIAL",
          unit_number: row.unitNumber,
          unit_label: row.unitLabel || row.unitNumber,
        },
      })
      continue
    }

    if (!row.id || !hasQuickUnitRowChanges(row)) continue

    updates.push({
      id: row.id,
      payload: {
        unit_label: row.unitLabel || row.unitNumber || "Wohneinheit",
      },
    })
  }

  return { creates, updates }
}

export const quickUnitRowsHaveValidationErrors = (rows: QuickPropertyUnitRow[]) =>
  rows.some((row) => Boolean(quickUnitRowValidationError(row)))

export const quickUnitRowsHaveChanges = (rows: QuickPropertyUnitRow[]) =>
  rows.some(hasQuickUnitRowChanges)
